import { env } from '@/env';
import { getRecordValue, isNonEmptyString, isRecord } from '@/lib/utils/type-guards';

const resolveTwitterDataSource = (): string | undefined => {
  if (globalThis.window === undefined) {
    return env.TWITTER_DATA_SOURCE ?? env.NEXT_PUBLIC_TWITTER_DATA_SOURCE ?? undefined;
  }
  return env.NEXT_PUBLIC_TWITTER_DATA_SOURCE ?? undefined;
};

const CONTEXT_MAP_PATTERN = /contextMap\[utilName]/i;

const KNOWN_ERROR_PATTERNS: Array<{ test: (message: string) => boolean; toMessage: () => string }> = [
  {
    test: (message) => CONTEXT_MAP_PATTERN.test(message),
    toMessage: () => {
      const deploymentSource = resolveTwitterDataSource();
      const graphqlEnabled = deploymentSource === 'graphql' || deploymentSource === 'hybrid';

      if (graphqlEnabled) {
        return 'Twitter data sync tried to use the GraphQL utilities, but this workspace is configured for the REST API. Update TWITTER_DATA_SOURCE or reconnect your Twitter account to continue.';
      }

      return 'Twitter data sync failed while contacting Twitter. Please retry the sync or reconnect your Twitter account to continue.';
    },
  },
];

const normalizeMessage = (message: string): string => {
  for (const matcher of KNOWN_ERROR_PATTERNS) {
    if (matcher.test(message)) {
      return matcher.toMessage();
    }
  }
  return message;
};

/**
 * Returns the most helpful message we can find on an unknown error object.
 * Falls back to the provided default when no useful copy is available.
 */
export function getErrorMessage(error: unknown, fallback = 'An unexpected error occurred'): string {
  if (error === undefined) {
    return normalizeMessage(fallback);
  }

  if (isNonEmptyString(error)) {
    return normalizeMessage(error.trim());
  }

  if (error instanceof Error) {
    const message = isNonEmptyString(error.message) ? error.message.trim() : fallback;
    return normalizeMessage(message);
  }

  if (isRecord(error)) {
    const candidateMessage = getRecordValue(error, 'message');
    if (isNonEmptyString(candidateMessage)) {
      return normalizeMessage(candidateMessage.trim());
    }

    const nestedError = getRecordValue(error, 'error');
    if (isNonEmptyString(nestedError)) {
      return normalizeMessage(nestedError.trim());
    }
  }

  return normalizeMessage(fallback);
}
